import { useState, useRef, useEffect, useCallback } from "react";
import { smoothScrollTo } from "../utils/scrollUtils";

interface UseChapterNavigationOptions {
  prefersReducedMotion: boolean;
  onPreviousChapter: () => void;
  onNextChapter: () => void;
  containerRef?: React.RefObject<HTMLElement | null>;
  contentRef?: React.RefObject<HTMLElement | null>;
  headerOffset?: number;
  watchIndex?: number; // Optional index to watch for changes
}

/**
 * Hook to standardize chapter navigation behavior across components
 * with enhanced smooth scrolling
 */
export const useChapterNavigation = ({
  prefersReducedMotion,
  onPreviousChapter,
  onNextChapter,
  containerRef,
  contentRef,
  headerOffset = 60,
  watchIndex,
}: UseChapterNavigationOptions) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationInProgress = useRef(false);
  const lastWatchedIndex = useRef(watchIndex);

  /**
   * Scrolls to the content element position after navigation
   * with optimized animation parameters in a single smooth motion
   */
  const scrollToContent = useCallback(() => {
    // If we have a content reference, use its position
    if (contentRef?.current) {
      // Duration based on screen size
      const scrollDuration = prefersReducedMotion
        ? 0
        : window.innerWidth >= 768
          ? 750 // Smooth animation on desktop
          : 650; // Slightly faster on mobile

      let targetPosition;

      // Calculate position based on container or window
      if (containerRef?.current) {
        // Get position relative to the scrolling container
        const containerRect = containerRef.current.getBoundingClientRect();
        const contentRect = contentRef.current.getBoundingClientRect();
        targetPosition = contentRect.top - containerRect.top - headerOffset;

        try {
          // For container scrolling, use direct scrollTo
          containerRef.current.scrollTo({
            top: targetPosition,
            behavior: prefersReducedMotion ? "auto" : "smooth",
          });
        } catch (e) {
          console.error("Error scrolling container:", e);
        }
      } else {
        // Get position relative to the window
        const rect = contentRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        // Round to integer to prevent sub-pixel rendering issues
        targetPosition = Math.round(rect.top + scrollTop - headerOffset);

        try {
          // Single smooth scroll with enhanced easing
          smoothScrollTo(
            targetPosition,
            scrollDuration,
            "easeInOutCubic", // Smooth acceleration/deceleration
          );
        } catch (e) {
          window.scrollTo({
            top: targetPosition,
            behavior: prefersReducedMotion ? "auto" : "smooth",
          });
        }
      }
      return;
    }

    // Fallback to the original behavior if no content ref is available
    const scrollDuration = prefersReducedMotion ? 0 : 750;

    try {
      smoothScrollTo(headerOffset, scrollDuration, "easeInOutCubic");
    } catch (e) {
      // Fallback to standard scrollTo if utility fails
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: headerOffset,
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      } else {
        window.scrollTo({
          top: headerOffset,
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      }
    }
  }, [prefersReducedMotion, contentRef, containerRef, headerOffset]);

  /**
   * Enhanced navigation with simplified single-step animation
   */
  const handlePreviousChapter = () => {
    // Prevent multiple navigations
    if (isNavigating || navigationInProgress.current) return;

    // Set navigation states
    setIsNavigating(true);
    navigationInProgress.current = true;

    // Call the handler immediately
    onPreviousChapter();

    // Scroll after navigation with just one step
    setTimeout(() => {
      scrollToContent();

      // Reset the navigating flags after animation should be complete
      // Add 50ms buffer to ensure animation is fully complete
      const animationTime = prefersReducedMotion ? 50 : 800;
      setTimeout(() => {
        navigationInProgress.current = false;
        setIsNavigating(false);
      }, animationTime); // Time for the full animation to complete
    }, 50); // Just enough delay for content to render
  };

  /**
   * Enhanced navigation with simplified single-step animation
   */
  const handleNextChapter = () => {
    // Prevent multiple navigations
    if (isNavigating || navigationInProgress.current) return;

    // Set navigation states
    setIsNavigating(true);
    navigationInProgress.current = true;

    // Call the handler immediately
    onNextChapter();

    // Scroll after navigation with just one step
    setTimeout(() => {
      scrollToContent();

      // Reset the navigating flags after animation should be complete
      // Add 50ms buffer to ensure animation is fully complete
      const animationTime = prefersReducedMotion ? 50 : 800;
      setTimeout(() => {
        navigationInProgress.current = false;
        setIsNavigating(false);
      }, animationTime); // Time for the full animation to complete
    }, 50); // Just enough delay for content to render
  };

  // Watch for chapter index changes to apply smooth scrolling
  // This handles cases when the chapter is changed externally (e.g., table of contents)
  useEffect(() => {
    if (watchIndex !== undefined && lastWatchedIndex.current !== watchIndex) {
      lastWatchedIndex.current = watchIndex;

      // Don't scroll immediately on initial render
      if (lastWatchedIndex.current !== undefined) {
        // Set navigating state to prevent other navigation during scroll
        setIsNavigating(true);
        navigationInProgress.current = true;

        // Short delay to let the component render with new content
        setTimeout(() => {
          scrollToContent();

          // Reset navigation flags after animation completes
          const animationTime = prefersReducedMotion ? 50 : 800;
          setTimeout(() => {
            navigationInProgress.current = false;
            setIsNavigating(false);
          }, animationTime);
        }, 50);
      }
    }
  }, [watchIndex, prefersReducedMotion, scrollToContent]);

  return {
    isNavigating,
    handlePreviousChapter,
    handleNextChapter,
  };
};

export default useChapterNavigation;
